import React, { useState, useEffect, useRef } from 'react';
import { Bot, Database, Eye, EyeOff, FileText, RefreshCw, Save, Search, Voicemail } from 'lucide-react';
import { useI18n } from '../../utils/i18n';
import apiService from '../../services/apiService';
import { useConfirm } from '../ui/ConfirmDialog';
import { useToast } from '../ToastProvider';
import { alleKiAus } from './FeaturesTab';
import Schalter from './Schalter';
import VoicemailEinstellungen, { voicemailNutzlast } from './VoicemailEinstellungen';

// #307: Taktung des Backfill-Fortschritts und wie viele Fehlschlaege in Folge
// hingenommen werden, bevor der Poll aufgibt (rund eine halbe Minute).
const BACKFILL_POLL_INTERVALL_MS = 3000;
const BACKFILL_POLL_MAX_FEHLVERSUCHE = 10;

// #312: Reiter „KI-Integration“ des Admin-Bereichs, Code unverändert aus
// AdminPanel.js übernommen (Muster und Begründung: admin/LogsTab.js).
//
// Mitgezogen ist der Aufräum-Effekt des Backfill-Polls aus #307. Weil das
// Panel diesen Hook aufruft, räumt er wie bisher beim Verlassen des
// Admin-Bereichs auf, nicht beim Reiterwechsel: Ein laufender Backfill wird
// weiter verfolgt, wenn man zwischendurch einen anderen Reiter öffnet.
export function useAiTab({ setError, setSaving }) {
  const { t } = useI18n();
  const confirm = useConfirm();
  const { pushToast } = useToast();
  // AI Integration State
  const [aiConfig, setAiConfig] = useState({ baseUrl: 'https://api.openai.com/v1', selectedModel: '', searchPrompt: "Use only the information provided in the context to answer the user's question. Keep the answer concise. Reply in the language of the context.", summaryPrompt: 'You are a helpful assistant. Given the ticket history context (emails, comments, Teams messages; no images), produce a concise, structured summary highlighting key points, decisions, open questions, and next steps. Reply in the language of the context.', perQueryFragmentCap: 60, globalFragmentCap: 300, contextCharCap: 12000, fullTicketsCount: 8 });
  const [aiModels, setAiModels] = useState([]);
  const [aiTesting, setAiTesting] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const apiKeyRef = useRef(null);
  // #14: Embeddings & Suchmodus
  const embeddingApiKeyRef = useRef(null);
  const [embStatus, setEmbStatus] = useState(null);
  const [embBusy, setEmbBusy] = useState(false);
  const [embTestResult, setEmbTestResult] = useState(null);
  // #307: Der Fortschritts-Poll des Backfills lief in einem Event-Handler,
  // ohne Ref und ohne Unmount-Aufraeumung -- verliess der Admin den Bereich,
  // pollte er bis zum Schliessen des Tabs weiter. Die Kennung gehoert in ein
  // Ref, damit ein Effekt sie beim Verlassen abraeumen kann.
  const backfillPollRef = useRef(null);

  // #307: einzige Aufgabe -- den Fortschritts-Poll beim Verlassen stoppen.
  // Die Debounce-Timer von Hinweisleiste und Releasenote (saveNotifTimerRef,
  // saveReleaseNoteTimerRef) werden hier BEWUSST NICHT abgeraeumt: Sie
  // speichern eine Eingabe nach 400 ms zum Server. Wer tippt und den Bereich
  // sofort verlaesst, wuerde seine Aenderung sonst still verlieren. Dort ist
  // das Weiterlaufen das gewuenschte Verhalten, kein Leck.
  useEffect(() => () => {
    // Bewusst nicht ueber stopBackfillPoll: Die Funktion entsteht bei jedem
    // Render neu und stuende sonst in der Abhaengigkeitsliste. Ein Ref zu
    // lesen ist hier alles, was gebraucht wird.
    try { if (backfillPollRef.current) clearInterval(backfillPollRef.current); } catch (_) {}
    backfillPollRef.current = null;
  }, []);

  const handleSaveAiConfig = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = {
        baseUrl: aiConfig.baseUrl,
        selectedModel: aiConfig.selectedModel,
        searchPrompt: aiConfig.searchPrompt,
        summaryPrompt: aiConfig.summaryPrompt,
        debugEnabled: !!aiConfig.debugEnabled,
        perQueryFragmentCap: parseInt(aiConfig.perQueryFragmentCap) || 60,
        globalFragmentCap: parseInt(aiConfig.globalFragmentCap) || 300,
        contextCharCap: parseInt(aiConfig.contextCharCap) || 12000,
        fullTicketsCount: parseInt(aiConfig.fullTicketsCount) || 8,
        // #520: searchAiEnabled, summaryAiEnabled, voicemailAiEnabled und mailAiMode
        // schaltet der Reiter „Features“. Sie fehlen hier mit Absicht: Mit einem
        // veralteten Stand im Formular würde „Speichern“ den Schalter zurückdrehen.
        transcribeModel: aiConfig.transcribeModel || '',
        transcribeApi: aiConfig.transcribeApi || 'auto',
        // #14: Embeddings & Suchmodus
        embeddingModel: aiConfig.embeddingModel || '',
        embeddingBaseUrl: aiConfig.embeddingBaseUrl || '',
        embeddingDimensions: (aiConfig.embeddingDimensions === '' || aiConfig.embeddingDimensions == null) ? null : (parseInt(aiConfig.embeddingDimensions) || null),
        retrievalMode: aiConfig.retrievalMode || 'lexical',
        // #574: nur, wenn der Server sie geliefert hat (sonst kennt das Formular sie nicht)
        ...(aiConfig.voicemailEinstellungen ? { voicemailEinstellungen: voicemailNutzlast(aiConfig.voicemailEinstellungen) } : {})
      };
      const apiKey = apiKeyRef.current?.value || '';
      if (apiKey && apiKey.trim()) payload.apiKey = apiKey.trim();
      const embKey = embeddingApiKeyRef.current?.value || '';
      if (embKey && embKey.trim()) payload.embeddingApiKey = embKey.trim();
      const resp = await apiService.updateAiIntegrationConfig(payload);
      setAiConfig(prev => ({ ...prev, ...(resp.config || {}) }));
      pushToast({ message: 'AI-Konfiguration gespeichert.', type: 'success' });
    } catch (e) {
      console.error('Save AI config error:', e);
      setError(t('admin.ai.saveFailed', 'Fehler beim Speichern der KI-Konfiguration.'));
    } finally {
      setSaving(false);
    }
  };

  // #14: Embedding-Provider testen (nutzt die GESPEICHERTE Konfiguration —
  // vorher speichern, wenn Modell/Provider geändert wurden)
  const handleTestEmbedding = async () => {
    setEmbBusy(true);
    setEmbTestResult(null);
    setError(null);
    try {
      const resp = await apiService.testEmbedding();
      setEmbTestResult(`OK — ${resp.model}, ${resp.dimensions} Dimensionen (${resp.durationMs} ms)`);
    } catch (e) {
      setEmbTestResult(`Fehler: ${e.response?.data?.error || e.message}${e.response?.data?.details ? ` — ${e.response.data.details}` : ''}`);
    } finally {
      setEmbBusy(false);
    }
  };

  // Liefert { ok, status }. Der Poll muss einen voruebergehenden Netzfehler
  // von der Aussage 'der Lauf ist beendet' unterscheiden koennen -- vorher
  // war beides `null` und ein einzelner Aussetzer beendete die Anzeige,
  // obwohl der Backfill weiterlief (#307).
  const fetchEmbeddingsStatus = async () => {
    try {
      const resp = await apiService.getEmbeddingsStatus();
      const status = resp.status || null;
      setEmbStatus(status);
      return { ok: true, status };
    } catch (_) {
      return { ok: false, status: null };
    }
  };

  const loadEmbeddingsStatus = async () => (await fetchEmbeddingsStatus()).status;

  const stopBackfillPoll = () => {
    try { if (backfillPollRef.current) clearInterval(backfillPollRef.current); } catch (_) {}
    backfillPollRef.current = null;
  };

  const handleStartBackfill = async () => {
    if (!(await confirm({ message: 'Backfill startet das Chunking des gesamten Bestands und schickt die Texte zum Einbetten an den konfigurierten Provider. Jetzt starten?' }))) return;
    setEmbBusy(true);
    setError(null);
    try {
      await apiService.startEmbeddingsBackfill();
      pushToast({ message: 'Backfill gestartet.', type: 'success' });
      // Fortschritt pollen, bis der Lauf beendet ist
      stopBackfillPoll();
      let fehlversuche = 0;
      backfillPollRef.current = setInterval(async () => {
        const ergebnis = await fetchEmbeddingsStatus();
        if (!ergebnis.ok) {
          // Ein Aussetzer darf den Lauf nicht fuer beendet erklaeren; der
          // Backfill laeuft serverseitig weiter. Erst nach mehreren
          // Fehlschlaegen in Folge aufgeben.
          if (++fehlversuche < BACKFILL_POLL_MAX_FEHLVERSUCHE) return;
          stopBackfillPoll();
          setEmbBusy(false);
          return;
        }
        fehlversuche = 0;
        const s = ergebnis.status;
        if (!s || !s.backfill || !s.backfill.running) {
          stopBackfillPoll();
          setEmbBusy(false);
        }
      }, BACKFILL_POLL_INTERVALL_MS);
    } catch (e) {
      setError(e.response?.data?.error || 'Backfill konnte nicht gestartet werden.');
      setEmbBusy(false);
    }
  };

  const handleTestAiConnection = async () => {
    setAiTesting(true);
    setError(null);
    try {
      const payload = { baseUrl: aiConfig.baseUrl, chatOnly: true };
      const apiKey = apiKeyRef.current?.value || '';
      if (apiKey && apiKey.trim()) payload.apiKey = apiKey.trim();
      const resp = await apiService.testAiIntegration(payload);
      setAiModels(resp.models || []);
      pushToast({ message: 'Verbindung OK. Modelle geladen.', type: 'success' });
    } catch (e) {
      console.error('AI test error:', e);
      setError(`Verbindungscheck fehlgeschlagen${e?.response?.data?.details ? ': ' + (e.response.data.details.error || e.response.data.details) : ''}`);
    } finally {
      setAiTesting(false);
    }
  };

  return { aiConfig, setAiConfig, aiModels, setAiModels, aiTesting, setAiTesting, showApiKey, setShowApiKey, apiKeyRef, embeddingApiKeyRef, embStatus, setEmbStatus, embBusy, setEmbBusy, embTestResult, setEmbTestResult, backfillPollRef, handleSaveAiConfig, handleTestEmbedding, fetchEmbeddingsStatus, loadEmbeddingsStatus, stopBackfillPoll, handleStartBackfill, handleTestAiConnection };
}

// #521: Ein Rahmen je Aufgabe. Vorher standen die Felder in der Reihenfolge,
// in der sie dazugekommen sind: der Schalter der KI-Suche zehn Felder unter
// ihrem Prompt, Base URL und API Key durch die Suchgrenzen getrennt.
//
// #520: Ist die Funktion im Reiter „Features“ aus, wird ihr Rahmen ausgegraut.
// Die Felder bleiben bedienbar, damit sich eine Funktion vorbereiten lässt,
// bevor man sie einschaltet.
const AiGruppe = ({ icon: Icon, titel, hinweis, aus, ausText, children }) => (
  <fieldset className={`ai-gruppe${aus ? ' ai-gruppe--aus' : ''}`}>
    <legend>{Icon && <Icon size={16} aria-hidden="true" />} {titel}</legend>
    {aus && <p className="ai-gruppe-aus">{ausText}</p>}
    {hinweis && <p className="ai-gruppe-hinweis">{hinweis}</p>}
    {children}
  </fieldset>
);



const AiTab = ({ tab, saving, features, users }) => {
  const { getLocale, t } = useI18n();
  // features = null (nicht geladen): nichts ausgrauen
  const aus = (name) => !!features && features[name] === false;
  const ausText = t('admin.ai.disabledInFeatures', 'Im Reiter „Features“ abgeschaltet. Die Einstellungen lassen sich trotzdem vorbereiten.');
  const { aiConfig, setAiConfig, aiModels, aiTesting, showApiKey, setShowApiKey, apiKeyRef, embeddingApiKeyRef, embStatus, embBusy, embTestResult, handleSaveAiConfig, handleTestEmbedding, loadEmbeddingsStatus, handleStartBackfill, handleTestAiConnection } = tab;

  return (
    <div className="admin-section">
      {/* #605: Speichern rechts neben der Überschrift, ohne Scrollen erreichbar */}
      <div className="admin-section-kopf" data-testid="ai-kopf">
        <h3>{t('admin.ai.title', 'AI Integration')}</h3>
        <div className="admin-section-aktionen">
          <span className="ai-speichern-hinweis">{t('admin.ai.saveAllHint', 'Speichert alle Rahmen auf einmal.')}</span>
          <button onClick={handleSaveAiConfig} className="btn btn-primary" disabled={saving}>
            <Save size={16} /> {saving ? t('common.saving', 'Speichere...') : t('common.save', 'Speichern')}
          </button>
        </div>
      </div>
      <p className="section-description">{t('admin.ai.sectionDescription', 'Konfigurieren Sie die Verbindung zu einem KI-Anbieter (OpenAI oder OpenRouter). Die OpenAI-Basis-URL ist vorausgefüllt.')} {t('admin.ai.switchesInFeatures', 'An- und abgeschaltet werden die Funktionen im Reiter „Features“.')}</p>

      <AiGruppe icon={Bot} titel={t('admin.ai.groupProvider', 'Anbieter-Verbindung')} hinweis={t('admin.ai.groupProviderHint', 'Gilt für alle KI-Funktionen darunter.')} aus={alleKiAus(features)} ausText={t('admin.ai.allDisabledInFeatures', 'Alle KI-Funktionen sind im Reiter „Features“ abgeschaltet.')}>
        <div className="form-row">
          <div className="form-group" style={{ flex: 1 }}>
            <label>{t('admin.ai.baseUrl', 'API Base URL')}</label>
            <input
              type="text"
              value={aiConfig.baseUrl || ''}
              onChange={(e) => setAiConfig({ ...aiConfig, baseUrl: e.target.value })}
              placeholder="https://api.openai.com/v1"
            />
            <div className="settings-help" style={{ marginTop: 6 }}>
              <small>{t('admin.ai.baseUrlExamples', 'Beispiele: https://api.openai.com/v1, https://openrouter.ai/api/v1')}</small>
            </div>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group" style={{ flex: 1 }}>
            <label>{t('admin.ai.apiKey', 'API Key')}</label>
            <div className="input-group">
              <input
                ref={apiKeyRef}
                type={showApiKey ? 'text' : 'password'}
                placeholder={aiConfig?.hasApiKey ? '••••••••••••' : 'sk-...'}
                autoComplete="new-password"
              />
              <button
                type="button"
                className="visibility-toggle"
                onClick={() => setShowApiKey(!showApiKey)}
                title={showApiKey ? t('admin.ai.hideKey', 'Ausblenden') : t('admin.ai.showKey', 'Anzeigen')}
                aria-label={showApiKey ? t('admin.ai.hideKey', 'Ausblenden') : t('admin.ai.showKey', 'Anzeigen')}
              >
                {showApiKey ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {aiConfig?.hasApiKey && (
              <div className="settings-help" style={{ marginTop: 6 }}>
                <small>{t('admin.ai.apiKeyStored', 'Ein API‑Key ist gespeichert.')}</small>
              </div>
            )}
          </div>
          <div className="form-group" style={{ width: 260 }}>
            <label>{t('admin.ai.model', 'Modell')}</label>
            <select
              value={aiConfig.selectedModel || ''}
              onChange={(e) => setAiConfig({ ...aiConfig, selectedModel: e.target.value })}
            >
              <option value="">{t('admin.ai.choosePlease', 'Bitte wählen…')}</option>
              {aiModels.map(m => (
                <option key={m.id} value={m.id}>{m.name || m.id}</option>
              ))}
            </select>
            <div className="settings-help" style={{ marginTop: 6 }}>
              <small>{t('admin.ai.modelsHint', 'Modelle werden über den Verbindungscheck geladen.')}</small>
            </div>
          </div>
          <div className="form-group" style={{ width: 260 }}>
            <label>{t('admin.ai.debug', 'Debug')}</label>
            <Schalter t={t} an={!!aiConfig.debugEnabled} onChange={(an) => setAiConfig({ ...aiConfig, debugEnabled: an })} />
          </div>
        </div>

        <div className="settings-actions ai-gruppe-aktionen">
          <button type="button" onClick={handleTestAiConnection} className="btn btn-secondary" disabled={aiTesting}>
            <RefreshCw size={16} /> {aiTesting ? t('admin.ai.testing', 'Teste…') : t('admin.ai.testConnection', 'Verbindung testen & Modelle laden')}
          </button>
        </div>
        {aiConfig?.lastValidationStatus && (
          <div className="settings-help" style={{ marginTop: 8 }}>
            <small>{t('admin.ai.lastCheck', 'Letzte Prüfung:')} {aiConfig.lastValidationStatus} {aiConfig.lastValidatedAt ? `(${new Date(aiConfig.lastValidatedAt).toLocaleString(getLocale())})` : ''}</small>
          </div>
        )}
      </AiGruppe>

      <AiGruppe icon={Search} titel={t('admin.ai.groupSearch', 'KI-Suche')} aus={aus('searchAi')} ausText={ausText}>
        <div className="form-row">
          <div className="form-group" style={{ width: 300 }}>
            <label>{t('admin.ai.retrievalMode', 'Suchmodus (KI-Suche)')}</label>
            <select
              value={aiConfig.retrievalMode || 'lexical'}
              onChange={(e) => setAiConfig({ ...aiConfig, retrievalMode: e.target.value })}
            >
              <option value="lexical">{t('admin.ai.retrievalLexical', 'Lexikalisch — wie bisher (Optimizer + Textsuche)')}</option>
              <option value="hybrid">{t('admin.ai.retrievalHybrid', 'Hybrid — Textsuche + semantisch (empfohlen nach Backfill)')}</option>
              <option value="vector">{t('admin.ai.retrievalVector', 'Semantisch — nur Embedding-Index')}</option>
            </select>
            <div className="settings-help" style={{ marginTop: 6 }}>
              <small>{t('admin.ai.retrievalHint', 'Hybrid/Semantisch erst nach erfolgreichem Backfill aktivieren (Suchindex unten).')}</small>
            </div>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group" style={{ flex: 1 }}>
            <label>{t('admin.ai.searchPrompt', 'Search Prompt (default English)')}</label>
            <textarea
              rows="3"
              value={aiConfig.searchPrompt || ''}
              onChange={(e) => setAiConfig({ ...aiConfig, searchPrompt: e.target.value })}
              placeholder="Use only the information provided in the context to answer the user's question. Keep the answer concise. Reply in the language of the context."
            />
            <div className="settings-help" style={{ marginTop: 6 }}>
              <small>{t('admin.ai.defaultHint', 'Voreinstellung wie gewünscht, kann hier angepasst werden.')}</small>
            </div>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group" style={{ width: 220 }}>
            <label>{t('admin.ai.perQueryFragmentCap', 'Fragmente pro Suchpaar')}</label>
            <input
              type="number"
              min="10"
              max="500"
              value={aiConfig.perQueryFragmentCap}
              onChange={(e) => setAiConfig({ ...aiConfig, perQueryFragmentCap: parseInt(e.target.value) })}
            />
            <div className="settings-help" style={{ marginTop: 6 }}>
              <small>{t('admin.ai.defaultValue', 'Standard: {{wert}}', { wert: 60 })}</small>
            </div>
          </div>
          <div className="form-group" style={{ width: 220 }}>
            <label>{t('admin.ai.globalFragmentCap', 'Globales Fragmentlimit')}</label>
            <input
              type="number"
              min="50"
              max="2000"
              value={aiConfig.globalFragmentCap}
              onChange={(e) => setAiConfig({ ...aiConfig, globalFragmentCap: parseInt(e.target.value) })}
            />
            <div className="settings-help" style={{ marginTop: 6 }}>
              <small>{t('admin.ai.defaultValue', 'Standard: {{wert}}', { wert: 300 })}</small>
            </div>
          </div>
          <div className="form-group" style={{ width: 220 }}>
            <label>{t('admin.ai.contextCharCap', 'Kontext-Zeichenlimit')}</label>
            <input
              type="number"
              min="2000"
              max="64000"
              step="500"
              value={aiConfig.contextCharCap}
              onChange={(e) => setAiConfig({ ...aiConfig, contextCharCap: parseInt(e.target.value) })}
            />
            <div className="settings-help" style={{ marginTop: 6 }}>
              <small>{t('admin.ai.defaultValue', 'Standard: {{wert}}', { wert: 12000 })}</small>
            </div>
          </div>
          <div className="form-group" style={{ width: 220 }}>
            <label>{t('admin.ai.fullTicketsCount', 'Anzahl Voll‑Tickets')}</label>
            <input
              type="number"
              min="0"
              max="20"
              value={aiConfig.fullTicketsCount}
              onChange={(e) => setAiConfig({ ...aiConfig, fullTicketsCount: parseInt(e.target.value) })}
            />
            <div className="settings-help" style={{ marginTop: 6 }}>
              <small>{t('admin.ai.defaultValue', 'Standard: {{wert}}', { wert: 8 })}</small>
            </div>
          </div>
        </div>
      </AiGruppe>

      <AiGruppe icon={Database} titel={t('admin.ai.groupIndex', 'Suchindex (Embeddings)')} hinweis={t('admin.ai.groupIndexHint', 'Grundlage für die Suchmodi „Hybrid“ und „Semantisch“.')} aus={aus('searchAi')} ausText={ausText}>
        <div className="form-row">
          <div className="form-group" style={{ width: 300 }}>
            <label>{t('admin.ai.embeddingModel', 'Embedding-Modell')}</label>
            <input
              type="text"
              value={aiConfig.embeddingModel || ''}
              onChange={(e) => setAiConfig({ ...aiConfig, embeddingModel: e.target.value })}
              placeholder="text-embedding-3-large"
            />
            <div className="settings-help" style={{ marginTop: 6 }}>
              <small>{t('admin.ai.embeddingModelHint', 'Leer = semantische Suche aus. Empfehlung: text-embedding-3-large.')}</small>
            </div>
          </div>
          <div className="form-group" style={{ width: 300 }}>
            <label>{t('admin.ai.embeddingBaseUrl', 'Embedding Base URL (optional)')}</label>
            <input
              type="text"
              value={aiConfig.embeddingBaseUrl || ''}
              onChange={(e) => setAiConfig({ ...aiConfig, embeddingBaseUrl: e.target.value })}
              placeholder="https://api.openai.com/v1"
            />
            <div className="settings-help" style={{ marginTop: 6 }}>
              <small>{t('admin.ai.embeddingProviderHint', 'Eigener Anbieter für /embeddings — nötig, wenn der Chat über OpenRouter läuft. Leer = Chat-Anbieter.')}</small>
            </div>
          </div>
          <div className="form-group" style={{ width: 300 }}>
            <label>{t('admin.ai.embeddingApiKey', 'Embedding API Key (optional)')}</label>
            <input
              ref={embeddingApiKeyRef}
              type="password"
              placeholder={aiConfig?.hasEmbeddingApiKey ? '••••••••••••' : t('admin.ai.embeddingApiKeyPlaceholder', 'sk-… (leer = Chat-Key)')}
              autoComplete="new-password"
            />
            {aiConfig?.hasEmbeddingApiKey && (
              <div className="settings-help" style={{ marginTop: 6 }}>
                <small>{t('admin.ai.embeddingApiKeyStored', 'Ein eigener Embedding-Key ist gespeichert.')}</small>
              </div>
            )}
          </div>
        </div>
        <div className="form-row">
          <div className="form-group" style={{ width: 220 }}>
            <label>{t('admin.ai.dimensions', 'Dimensionen (optional)')}</label>
            <input
              type="number"
              min="256"
              max="4096"
              value={aiConfig.embeddingDimensions || ''}
              onChange={(e) => setAiConfig({ ...aiConfig, embeddingDimensions: e.target.value })}
              placeholder={t('admin.ai.dimensionsPlaceholder', 'Modell-Standard')}
            />
            <div className="settings-help" style={{ marginTop: 6 }}>
              <small>{t('admin.ai.dimensionsHint', 'z. B. 1024 spart zwei Drittel Speicher bei text-embedding-3-large. Nach einer Änderung: Backfill!')}</small>
            </div>
          </div>
          <div className="form-group" style={{ flex: 1 }}>
            <label>{t('admin.ai.indexStatus', 'Index-Status')}</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <button type="button" className="btn btn-secondary btn-sm" onClick={loadEmbeddingsStatus}>
                <RefreshCw size={14} /> {t('admin.ai.loadStatus', 'Status laden')}
              </button>
              <button type="button" className="btn btn-secondary btn-sm" onClick={handleTestEmbedding} disabled={embBusy}>
                {t('admin.ai.testEmbedding', 'Embedding testen')}
              </button>
              <button type="button" className="btn btn-primary btn-sm" onClick={handleStartBackfill} disabled={embBusy || !!embStatus?.backfill?.running}>
                {embStatus?.backfill?.running ? t('admin.ai.backfillRunning', 'Backfill läuft…') : t('admin.ai.backfillStart', 'Backfill starten')}
              </button>
            </div>
            {embStatus && (
              <div className="settings-help" style={{ marginTop: 6 }}>
                <small>
                  {t('admin.ai.chunksEmbedded', '{{eingebettet}}/{{gesamt}} Chunks eingebettet', { eingebettet: embStatus.embeddedChunks, gesamt: embStatus.totalChunks })}
                  {embStatus.pendingChunks > 0 ? ` ${t('admin.ai.chunksPending', '({{anzahl}} wartend)', { anzahl: embStatus.pendingChunks })}` : ''}
                  {embStatus.backfill?.running ? ` — ${t('admin.ai.backfillProgress', 'Backfill: {{phase}}, {{quellen}} Quellen, {{eingebettet}} eingebettet', { phase: embStatus.backfill.phase, quellen: embStatus.backfill.sources, eingebettet: embStatus.backfill.embedded })}` : ''}
                  {embStatus.backfill?.lastError ? ` — ${t('admin.ai.backfillLastError', 'letzter Fehler:')} ${embStatus.backfill.lastError}` : ''}
                </small>
              </div>
            )}
            {embTestResult && (
              <div className="settings-help" style={{ marginTop: 6 }}>
                <small>{embTestResult}</small>
              </div>
            )}
          </div>
        </div>
      </AiGruppe>

      <AiGruppe icon={FileText} titel={t('admin.ai.groupSummary', 'KI-Zusammenfassung')} aus={aus('summaryAi')} ausText={ausText}>
        <div className="form-row">
          <div className="form-group" style={{ flex: 1 }}>
            <label>{t('admin.ai.summaryPrompt', 'Summary Prompt (default English)')}</label>
            <textarea
              rows="3"
              value={aiConfig.summaryPrompt || ''}
              onChange={(e) => setAiConfig({ ...aiConfig, summaryPrompt: e.target.value })}
              placeholder="You are a helpful assistant. Given the ticket history context (emails, comments, Teams messages; no images), produce a concise, structured summary highlighting key points, decisions, open questions, and next steps. Reply in the language of the context."
            />
            <div className="settings-help" style={{ marginTop: 6 }}>
              <small>{t('admin.ai.summaryPromptHint', 'Vorgabetext für die Ticket-Zusammenfassung in der Detailansicht.')}</small>
            </div>
          </div>
        </div>
      </AiGruppe>

      {/* #520: Der Rahmen „KI-Ticketerstellung aus Mails“ (#36) entfällt. Sein
          einziges Feld war der Modus aus/Schatten/scharf, und der ist jetzt der
          Schalter im Reiter „Features“. */}
      <AiGruppe icon={Voicemail} titel={t('admin.ai.groupVoicemail', 'Anrufbeantworter (Voicemail)')} aus={aus('voicemailAi')} ausText={ausText}>
        <div className="form-row">
          <div className="form-group" style={{ width: 300 }}>
            <label>{t('admin.ai.transcribeModel', 'Transkriptions-Modell (Anrufbeantworter)')}</label>
            <input
              type="text"
              value={aiConfig.transcribeModel || ''}
              onChange={(e) => setAiConfig({ ...aiConfig, transcribeModel: e.target.value })}
              placeholder={t('admin.ai.transcribePlaceholder', 'google/gemini-3.5-flash-lite (Standard)')}
            />
            <div className="settings-help" style={{ marginTop: 6 }}>
              <small>{t('admin.ai.transcribeHint', 'Audiofähiges Modell für die Voicemail-Transkription. Leer = Standard.')}</small>
            </div>
          </div>
          {/* #507: chat = /chat/completions (multimodale Chat-Modelle), audio =
              /audio/transcriptions (reine Transkriptionsmodelle). Automatisch
              entscheidet die Modellliste des Anbieters. */}
          <div className="form-group" style={{ width: 300 }}>
            <label>{t('admin.ai.transcribeApi', 'Transkriptions-Schnittstelle')}</label>
            <select
              value={aiConfig.transcribeApi || 'auto'}
              onChange={(e) => setAiConfig({ ...aiConfig, transcribeApi: e.target.value })}
            >
              <option value="auto">{t('admin.ai.transcribeApiAuto', 'Automatisch nach Modell')}</option>
              <option value="chat">{t('admin.ai.transcribeApiChat', 'Chat (/chat/completions, audiofähige Chat-Modelle)')}</option>
              <option value="audio">{t('admin.ai.transcribeApiAudio', 'Transkription (/audio/transcriptions, z. B. openai/gpt-transcribe)')}</option>
            </select>
            <div className="settings-help" style={{ marginTop: 6 }}>
              <small>{t('admin.ai.transcribeApiHint', 'Automatisch: Führt der Anbieter das Modell als Transkriptionsmodell, geht es über /audio/transcriptions, sonst über den Chat.')}</small>
            </div>
          </div>
        </div>
        {/* #574: Erkennung, Sprache, Prompt und Ticket-Vorgaben der Pipeline */}
        <VoicemailEinstellungen
          einst={aiConfig.voicemailEinstellungen}
          vorgaben={aiConfig.voicemailVorgaben}
          users={users}
          onChange={(v) => setAiConfig({ ...aiConfig, voicemailEinstellungen: v })}
        />
      </AiGruppe>
    </div>
  );
};

export default AiTab;
